import { makeAutoObservable, reaction, runInAction } from 'mobx';
import logger from '../helpers/logger';
import api from '../api/api';
import { AccountLookupRequestType } from '../typegen/account-lookup-request-type';
import { Form } from '../typegen/form';
import { CardType } from '../typegen/card-type';
import { InvoiceType } from '../typegen/invoice-type';
import { PayorInformation } from '../typegen/payor-information';
import { ReceiptType } from '../typegen/receipt-type';
import { ICartItem, CartItem } from '../types/CartItem';
import { AccountLookupResponse } from '../typegen/account-lookup-response';
import { KioEMVResult } from '../devices/emv-device/EMVDevice';
import { PaymentType } from '../typegen/payment-type';
import { store } from './store';
import { Customer } from '../typegen/customer';
import { resetAccountSelectionViewState } from '../helpers/accountSelectionViewState';
import { getCISBehavior } from '../integrations';

export default class AccountStore {
	private _latestLookupResponse: AccountLookupResponse | null = null;
	private _cartItemRegistry = new Map<string, CartItem>();
	private _invoiceType?: InvoiceType;
	private _cardType?: CardType;
	private _payorInformation?: PayorInformation;
	private _digitalReceiptDestination?: { email?: string; phone?: string };
	private _receiptType?: ReceiptType;
	private _paymentType?: PaymentType;
	private _focusedCartItemId: string | null = null;
	private _emvResult?: KioEMVResult | null = null;
	private _cashEnabled?: boolean = true;
	private _cardEnabled?: boolean = true;
	private _checkEnabled?: boolean = true;
	private _cartId?: string; // for grant street
	private _token?: string; // for grant street
	private _transactionFee?: string;
	private _transactionTotalWithFee?: string;
	private _hasPreparedTransaction: boolean = false;
	private _isSavingCheckInfo: boolean = false;
	private _barcodeData: string | null = null;
	private _thankYouViewState: string | null = null; // 'ThankYou' | 'GetCheck' | null
	private _kioskPaymentId: string | null = null;
	private _preReceiptCheckRemovalPromptHandled: boolean = false;
	private _lookupGeneration: number = 0;

	get thankYouViewState() {
		return this._thankYouViewState;
	}

	setThankYouViewState = (state: string | null) => {
		this._thankYouViewState = state;
	};

	get preReceiptCheckRemovalPromptHandled() {
		return this._preReceiptCheckRemovalPromptHandled;
	}

	setPreReceiptCheckRemovalPromptHandled = (handled: boolean) => {
		this._preReceiptCheckRemovalPromptHandled = handled;
	};

	constructor() {
		makeAutoObservable(this);

		reaction(
			// Log whenever cart items change
			() => this.cartItems.map((item) => item.id),
			(newIds, oldIds) => {
				const added = newIds.filter((id) => !oldIds.includes(id));
				const removed = oldIds.filter((id) => !newIds.includes(id));
				if (added.length > 0) logger.logGeneral.log({}, 'Cart items added:', added);
				if (removed.length > 0) logger.logGeneral.log({}, 'Cart items removed:', removed);
			}
		);

		reaction(
			// Track amountToPay for each cart item
			() => this.cartItems.map((item) => ({ id: item.id, amountToPay: item.amountToPay })),
			(newAmounts, oldAmounts) => {
				newAmounts.forEach((newItem) => {
					const oldItem = oldAmounts.find((o) => o.id === newItem.id);
					if (oldItem && oldItem.amountToPay !== newItem.amountToPay) {
						logger.logGeneral.log(
							{},
							`Cart item [${newItem.id}] amountToPay changed from ${oldItem.amountToPay} to ${newItem.amountToPay}`
						);
					}
				});
			}
		);
	}

	/**
	 * Gets the lookup items from the latest lookup response and transforms them into CartItem objects.
	 *
	 * @returns An array of CartItem objects created from the account items in the latest lookup response.
	 *          Returns an empty array if no lookup response or account items are available.
	 *
	 * @remarks
	 * Each CartItem is constructed with:
	 * - id: The item number from the original item
	 * - item: The original item object
	 * - amountToPay: The amount due for the item
	 * - customer: The customer from the account in the lookup response
	 * - isAmountConfirmed: Always set to false initially
	 */
	get lookupItems(): ICartItem[] {
		if (!this._latestLookupResponse?.account?.items) return [];
		return this._latestLookupResponse.account.items.map((item): ICartItem => {
			return {
				id: item.itemNumber + (item.taxYear ?? ''), // Append tax year to ID to ensure uniqueness if multiple items have the same item number
				item,
				amountToPay: item.amountDue,
				taxYear: item.taxYear,
				isInactiveAccount: item.isInactiveAccount,
				customer:
					this._latestLookupResponse!.account?.customer ||
					(item.name || item.address
						? ({
								customerId: item.itemNumber,
								name: item.name || item.address,
							} as Customer)
						: undefined),
				isAmountConfirmed: false,
			};
		});
	}

	get showEscrowNotice(): boolean {
		return this.cartItems.some((item) => item.item.amountDue === 0);
	}

	get hasPrePayLookupAccounts(): boolean {
		const lookupHasPrePay = this._latestLookupResponse?.account?.items?.some((item) => item.isPrePay) ?? false;
		const cartHasPrePay = this.cartItems.some((item) => item.item.isPrePay);
		return lookupHasPrePay || cartHasPrePay;
	}

	get invoiceType() {
		return this._invoiceType;
	}

	get cardType() {
		return this._cardType;
	}

	get payorInformation() {
		return this._payorInformation;
	}

	get receiptType() {
		return this._receiptType;
	}

	get digitalReceiptDestination() {
		return this._digitalReceiptDestination;
	}

	get cartItems() {
		return Array.from(this._cartItemRegistry.values());
	}

	get cartTotalAmount() {
		return this.cartItems.reduce((total, item) => total + Math.max(0, item.amountToPay || 0), 0);
	}

	get focusedCartItemId() {
		return this._focusedCartItemId;
	}

	get focusedCartItem() {
		if (!this.focusedCartItemId) return null;
		return this._cartItemRegistry.get(this.focusedCartItemId) || null;
	}

	get paymentType() {
		return this._paymentType;
	}

	get emvResult() {
		return this._emvResult;
	}

	get cashEnabled() {
		return this._cashEnabled;
	}

	get cardEnabled() {
		return this._cardEnabled;
	}

	get checkEnabled() {
		return this._checkEnabled;
	}

	get transactionFee() {
		return this._transactionFee;
	}

	get transactionTotalWithFee() {
		return this._transactionTotalWithFee;
	}

	get cartId() {
		return this._cartId;
	}

	get token() {
		return this._token;
	}

	get hasPreparedTransaction() {
		return this._hasPreparedTransaction;
	}

	get isSavingCheckInfo() {
		return this._isSavingCheckInfo;
	}

	/**
	 * Gets the latest barcode data stored. This is used when the global barcode scanner is not in use in the scan barcode page.
	 */
	get barcodeData() {
		return this._barcodeData;
	}

	get savedCheckDetails() {
		return {
			savedACHLastAccountType: this._latestLookupResponse?.account?.savedACHLastAccountType ?? 'Checking',
			savedACHLastFour: this._latestLookupResponse?.account?.savedACHLastFour ?? '',
		};
	}

	get kioskPaymentId() {
		return this._kioskPaymentId;
	}

	setKioskPaymentId = (id: string | null | undefined) => {
		this._kioskPaymentId = id ?? null;
	};

	clearBarcodeData = () => {
		this._barcodeData = null;
	};

	areAllCartItemsConfirmed = () => {
		return Array.from(this._cartItemRegistry.values()).every((item) => item.isAmountConfirmed);
	};

	setBarcodeData = (data: string) => {
		logger.debug.log('Setting barcode data in AccountStore:', data);
		this._barcodeData = data;
	};

	clearCart = () => {
		this._cartItemRegistry.clear();
		this._preReceiptCheckRemovalPromptHandled = false;
		this._kioskPaymentId = null;
	};

	confirmCartItem = (id: string) => {
		try {
			const item = this._cartItemRegistry.get(id);
			item!.isAmountConfirmed = true;
		} catch (error) {
			logger.logGeneral.error({}, `Failed to confirm cart item with id ${id}`, 'confirmCartItem error', error);
		}
	};

	confirmAllCartItems = () => {
		this._cartItemRegistry.forEach((item) => {
			item.isAmountConfirmed = true;
		});
	};

	setReceiptType = (type: ReceiptType) => {
		this._receiptType = type;
	};

	setFocusedAndSelectedCartItemId = (id: string) => {
		this._focusedCartItemId = id;
	};

	setDigitalReceiptDestination = (email: string | null, phone?: string) => {
		this._digitalReceiptDestination = { email: email || undefined, phone };
	};

	setInvoiceType = (type: InvoiceType) => {
		this._invoiceType = type;
	};

	setPaymentType = (type: PaymentType) => {
		this._paymentType = type;
	};

	setCardType = (type: CardType) => {
		this._cardType = type;
	};

	setPayorInformation = (info: PayorInformation) => {
		this._payorInformation = info;
	};

	setEMVResult = (result: KioEMVResult | null) => {
		this._emvResult = result;
	};

	setCashEnabled = (enabled: boolean) => {
		this._cashEnabled = enabled;
	};

	setCardEnabled = (enabled: boolean) => {
		this._cardEnabled = enabled;
	};

	setCheckEnabled = (enabled: boolean) => {
		this._checkEnabled = enabled;
	};

	setTransactionFee = (fee: string) => {
		this._transactionFee = fee;
	};

	setTransactionTotalWithFee = (total: string) => {
		this._transactionTotalWithFee = total;
	};

	setCartId = (id: string) => {
		this._cartId = id;
	};

	setToken = (token: string) => {
		this._token = token;
	};

	setHasPreparedTransaction = (value: boolean) => {
		this._hasPreparedTransaction = value;
	};

	setIsSavingCheckInfo = (value: boolean) => {
		this._isSavingCheckInfo = value;
	};

	clearLastLookupResponse = () => {
		this._latestLookupResponse = null;
	};

	clearFocusedCartItemId = () => {
		this._focusedCartItemId = null;
	};

	setCartItemAmountToPay = (id: string, amount: number) => {
		try {
			const item = this._cartItemRegistry.get(id);
			item!.amountToPay = amount;
		} catch (error) {
			logger.logGeneral.error({}, `Failed to set amount to pay for cart item with id ${id}`, 'setCartItemAmountToPay error', error);
		}
	};

	addToCart = (id: string) => {
		const removePreviousAddedItem = !store.configStore.allowMultipleAccountSelection;

		try {
			runInAction(() => {
				if (removePreviousAddedItem) {
					this.lookupItems.forEach((lookupItem) => {
						if (this._cartItemRegistry.has(lookupItem.id)) {
							this._cartItemRegistry.delete(lookupItem.id);
						}
					});
				}

				const item = this.lookupItems.find((i) => i.id === id);
				if (item) {
					this._cartItemRegistry.set(item.id, new CartItem(item));
					logger.debug.log('Added to cart:', item);
					logger.debug.log('Current cart items:', Array.from(this._cartItemRegistry.values()));
				}
			});
		} catch (error) {
			logger.logGeneral.error({}, `Failed to add item with id ${id} to cart`, 'addToCart error', error);
		}
	};

	removeFromCart = (id: string) => {
		logger.debug.log('Removing from cart item with id:', id);
		runInAction(() => {
			this._cartItemRegistry.delete(id);
		});
	};

	private lookupByAccountNumberAndLastName = async (
		accountNumber: string,
		lastName?: string,
		isBarcodeLookup: boolean = false,
		lookupGeneration?: number
	) => {
		try {
			const response = await api.lookups.lookupAccountNumber({ accountNumber, lastName, isBarcodeLookup });
			if (lookupGeneration !== undefined && lookupGeneration !== this._lookupGeneration) {
				logger.logCIS.log({}, 'Ignoring stale account number lookup response', { lookupGeneration, current: this._lookupGeneration });
				return;
			}
			logger.logCIS.log({}, 'Account lookup by number and last name successful', response);
			this._latestLookupResponse = response ?? null;
		} catch (error) {
			logger.logCIS.error({}, 'Failed to lookup account by number and last name', error);
		}
	};

	private lookupByAddressAndLastName = async (address: string, lastName: string, lookupGeneration?: number) => {
		try {
			const response = await api.lookups.lookupAddressLastName({ address, lastName });
			if (lookupGeneration !== undefined && lookupGeneration !== this._lookupGeneration) {
				logger.logCIS.log({}, 'Ignoring stale address lookup response', { lookupGeneration, current: this._lookupGeneration });
				return;
			}
			logger.logCIS.log({}, 'Account lookup by address and last name successful', response);
			this._latestLookupResponse = response ?? null;
		} catch (error) {
			logger.logCIS.error({}, 'Failed to lookup account by address and last name', error);
		}
	};

	private lookupByPhoneAndLastName = async (phone: string, lastName: string, lookupGeneration?: number) => {
		try {
			const response = await api.lookups.lookupPhoneLastName({ phoneNumber: phone, lastName });
			if (lookupGeneration !== undefined && lookupGeneration !== this._lookupGeneration) {
				logger.logCIS.log({}, 'Ignoring stale phone lookup response', { lookupGeneration, current: this._lookupGeneration });
				return;
			}
			logger.logCIS.log({}, 'Account lookup by phone and last name successful', response);
			this._latestLookupResponse = response ?? null;
		} catch (error) {
			logger.logCIS.error({}, 'Failed to lookup account by phone and last name', error);
		}
	};

	private togglePaymentMethods(accounts: AccountLookupResponse) {
		const cisToggles = getCISBehavior(store.configStore.cisType).getLookupPaymentMethodToggles?.(accounts.account?.items ?? [], (message, data) =>
			logger.logCIS.log({}, message, data)
		);
		if (cisToggles) {
			this.setCashEnabled(cisToggles.cash);
			this.setCardEnabled(cisToggles.card);
			this.setCheckEnabled(cisToggles.check);
			return;
		}

		accounts.account?.items.forEach((item) => {
			item.paymentMethods?.forEach((method) => {
				switch (method.paymentType) {
					case PaymentType.Cash:
						if (!method.isAllowed) this.setCashEnabled(false);
						break;
					case PaymentType.Card:
						if (!method.isAllowed) this.setCardEnabled(false);
						break;
					case PaymentType.Check:
						if (!method.isAllowed) this.setCheckEnabled(false);
						break;
				}
			});
		});
	}

	lookupAccount = async (requestType: AccountLookupRequestType, data: Record<string, string>, form?: Form | null) => {
		logger.logCIS.log({}, 'Starting account lookup', { 'request-type': requestType ?? null, data });

		try {
			const lookupGeneration = ++this._lookupGeneration;
			resetAccountSelectionViewState();
			// Break Confirm protection so a failed lookup cannot restore "Accounts to be Paid"
			store.uiStore.setOverrideConfirmView(true);

			if (!store.configStore.allowMultipleAccountSelection) {
				this.clearCart();
			}
			store.accountStore.clearLastLookupResponse();
			switch (requestType) {
				case AccountLookupRequestType.AccountNumberAndLastName: {
					const accountNumber =
						data.accountNumber ?? data['account-number'] ?? (form?.fields && form.fields[0] ? data[form.fields[0].name] : '');
					const lastName = data.lastName ?? data['last-name'];

					if (!accountNumber) {
						logger.logCIS.warn({}, 'No account number provided.');
						return;
					}

					await this.lookupByAccountNumberAndLastName(accountNumber, lastName, false, lookupGeneration);
					break;
				}
				case AccountLookupRequestType.StreetNumberAndLastName: {
					const address = data.address ?? data['street-number'];
					const lastName = data.lastName ?? data['last-name'];

					if (!address || !lastName) {
						logger.logCIS.warn({}, 'Address and last name are required for address lookup.');
						return;
					}

					await this.lookupByAddressAndLastName(address, lastName, lookupGeneration);
					break;
				}
				case AccountLookupRequestType.PhoneAndLastName: {
					const phone = data.phone ?? data['phone-number'];
					const lastName = data.lastName ?? data['last-name'];

					if (!phone) {
						logger.logCIS.warn({}, 'Phone number is required for phone lookup.');
						return;
					}

					await this.lookupByPhoneAndLastName(phone, lastName, lookupGeneration);
					break;
				}
				case AccountLookupRequestType.AccountNumber: {
					const accountNumber =
						data.accountNumber ?? data['account-number'] ?? (form?.fields && form.fields[0] ? data[form.fields[0].name] : '');

					if (!accountNumber) {
						logger.logCIS.warn({}, 'No account number provided.');
						return;
					}

					await this.lookupByAccountNumberAndLastName(accountNumber, undefined, false, lookupGeneration);
					break;
				}
				case AccountLookupRequestType.Barcode: {
					const accountNumber =
						data.accountNumber ?? data['account-number'] ?? (form?.fields && form.fields[0] ? data[form.fields[0].name] : '');

					if (!accountNumber) {
						logger.logCIS.warn({}, 'No account number provided.');
						return;
					}

					await this.lookupByAccountNumberAndLastName(accountNumber, undefined, true, lookupGeneration);
					break;
				}
				default:
					throw new Error(`Account lookup request type ${requestType} is not implemented.`);
			}
			if (lookupGeneration !== this._lookupGeneration) {
				logger.logCIS.log({}, 'Skipping payment method toggle for stale lookup', { lookupGeneration, current: this._lookupGeneration });
				return;
			}
			if (this._latestLookupResponse) {
				this.togglePaymentMethods(this._latestLookupResponse);
			}
		} catch (error) {
			logger.logCIS.error({}, 'Account lookup failed', error);
		}
	};

	handleGlobalBarcodeScan = (accountNumber: string) => {
		this.setBarcodeData(accountNumber);
	};
}
